import axios from 'axios';
import env from '@/config/env';
import { getToken, clearSession } from '@/utils/storage';
import { mockRequest } from '@/mock';

// The one configured axios instance. Every *.service.js goes through here.
const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the admin Bearer token to every request.
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Normalize the backend envelope: resolve to response.data, reject with { code, message, status }.
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const normalized = {
      status,
      code: body?.error?.code || 'NETWORK_ERROR',
      message: body?.error?.message || error.message || 'Request failed',
      messageHi: body?.error?.messageHi,
    };
    // 401 → session is dead. Clear and bounce to login (unless we're already there).
    if (status === 401) {
      clearSession();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(normalized);
  },
);

// Public client. When VITE_USE_MOCKS is on, requests are served by the in-memory mock engine
// (src/mock) which returns the exact shapes documented in MISSING_APIS.md.
const request = (method, url, { params, data, ...rest } = {}) => {
  if (env.useMocks) return mockRequest({ method, url, params, data });
  // `rest` carries per-request axios overrides (e.g. timeout for slow AI calls).
  return axiosInstance.request({ method, url, params, data, ...rest });
};

const apiClient = {
  get: (url, config = {}) => request('get', url, config),
  post: (url, data, config = {}) => request('post', url, { ...config, data }),
  put: (url, data, config = {}) => request('put', url, { ...config, data }),
  patch: (url, data, config = {}) => request('patch', url, { ...config, data }),
  delete: (url, config = {}) => request('delete', url, config),
  raw: axiosInstance,
};

export default apiClient;
